import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import MaskedTextField from '@/common/components/MaskedTextField'
import SelectController from '@/common/components/SelectController'
import Spinner from '@/common/components/Spinner'
import TextField from '@/common/components/TextField'
import { nameMask, phoneMask } from '@/common/constants/masks.constant'
import { useRoles } from '@/common/hooks/queries/useRoles'
import { useGoBack } from '@/common/hooks/useGoBack'
import { Mapper } from '@/common/services/mapper'
import { RoleData } from '@/common/types/roles'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useEstablishmentUser } from '@/features/users/hooks/useEstablishmentUser'
import { useUpdateEstablishmentUser } from '@/features/users/hooks/useUpdateEstablishmentUser'
import {
  establishmentUserSchema,
  EstablishmentUserValidationSchema,
} from '@/features/users/schemas/create.schema'
import { establishmentUserDefaultValues } from '@/features/users/utils/establishmentUserDefaultValues'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronLeftIcon } from 'lucide-react'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { statusOptions } from '../../constants/statusOptions.constant'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { ImageUploader } from '@/common/components/ImageUploader'
import { checkIsImage } from '@/common/utils/fileValidator'
import useDebounce from '@/common/hooks/useDebounce'

const Update = () => {
  const { id: establishmentUserId } = useParams()
  const goBack = useGoBack()
  const queryClient = useQueryClient()
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [file, setFile] = useState<Blob>();

  const [searchRole, setSearchRole] = useDebounce('');

  const { data: establishmentUserData } = useEstablishmentUser({
    id: establishmentUserId || '',
  })

  const { data: rolesOptions } = useRoles({
    filters: { search: searchRole },
    mapper: Mapper.mapToOptions<RoleData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  })

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors },
  } = useForm<EstablishmentUserValidationSchema>({
    resolver: zodResolver(establishmentUserSchema),
    defaultValues: establishmentUserDefaultValues(establishmentUserData),
  })

  const {
    mutate: updateEstablishmentUser,
    isPending: updateEstablishmentUserIsPending,
  } = useUpdateEstablishmentUser({
    establishmentUserId: establishmentUserId,
    establishmentUserData,
    file,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['establishmentUser', establishmentUserId],
      })
      goBack({ fallback: `/users/${establishmentUserId}` })
    },
    onError: FormTransformers.errorsTransformer<EstablishmentUserValidationSchema>({
      setError,
      setMessages,
    }),
  })

  const onSubmit: SubmitHandler<EstablishmentUserValidationSchema> = (data) => {
    updateEstablishmentUser(data)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-4 items-center">
          <Button
            variant="link-white"
            onClick={() =>
              goBack({ fallback: `/users/${establishmentUserId}` })
            }
          >
            <ChevronLeftIcon size={40} strokeWidth={1} />
          </Button>
          <h2 className="text-2xl font-bold">
            {establishmentUserData?.full_name}
          </h2>
        </div>
        <Button
          type="submit"
          className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
          disabled={updateEstablishmentUserIsPending}
        >
          {updateEstablishmentUserIsPending ? (
            <Spinner className="h-6 mx-[23px] fill-white-800" />
          ) : (
            'Confirmar'
          )}
        </Button>
      </div>
      <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto max-h-[calc(100vh-128px)]">
        <p className="text-lg font-bold">Dados pessoais</p>
        <ImageUploader
          isLoading={false}
          label="Foto"
          name="profile-picture"
          accept="image/*"
          onChange={(e) => {
            const file = (e.target as HTMLInputElement & { files: FileList }).files?.[0]
            if (!file || !checkIsImage(file)) {
              return
            }
            setFile((e.target as HTMLInputElement & { files: FileList }).files[0])
          }}
          currentImageUrl={file
            ? URL.createObjectURL(file)
            : establishmentUserData?.profile_picture?.url
              ? establishmentUserData?.profile_picture?.url
              : undefined
          }
          disabled={updateEstablishmentUserIsPending}
          errorMessage={errors.profile_picture?.message as string | undefined}
        />
        <div className="flex gap-2 w-full justify-between">
          <div className="w-3/4">
            <MaskedTextField
              label="CPF"
              markAsRequired
              mask="000.000.000-00"
              errorMessage={errors?.cpf?.message || undefined}
              {...register('cpf')}
              disabled={updateEstablishmentUserIsPending}
            />
          </div>
          <MaskedTextField
            label="Data de nascimento"
            markAsRequired
            mask="00/00/0000"
            className="w-1/4"
            errorMessage={errors.date_of_birth?.message || undefined}
            {...register('date_of_birth')}
            disabled={updateEstablishmentUserIsPending}
          />
        </div>
        <div className="flex gap-2 w-full justify-between">
          <MaskedTextField
            label="Nome"
            markAsRequired
            mask={nameMask}
            className="w-3/4"
            errorMessage={errors.full_name?.message || undefined}
            {...register('full_name')}
            disabled={updateEstablishmentUserIsPending}
          />
          <SelectController
            control={control}
            name="user_roles"
            label="Perfil"
            markAsRequired
            className="w-1/4"
            errorMessage={errors.user_roles?.message || undefined}
            options={rolesOptions || []}
            onInputChange={setSearchRole}
            isMulti
            isDisabled={updateEstablishmentUserIsPending}
          />
        </div>
        <SelectController
          control={control}
          name="status"
          label="Status"
          markAsRequired
          errorMessage={errors.status?.message || undefined}
          options={statusOptions}
          isDisabled={updateEstablishmentUserIsPending}
        />
        <TextField
          label="Matrícula"
          errorMessage={errors.enrollment_number?.message || undefined}
          {...register('enrollment_number')}
          disabled={updateEstablishmentUserIsPending}
        />
        <MaskedTextField
          label="Telefone"
          mask={phoneMask}
          errorMessage={errors.phone?.number?.message || undefined}
          {...register('phone.number')}
          disabled={updateEstablishmentUserIsPending}
        />
        <TextField
          label="Email"
          {...register('email')}
          errorMessage={errors.email?.message || undefined}
          disabled={updateEstablishmentUserIsPending}
        />
      </Box>

      {messages && (
        <ModalMessageSimple
          messages={messages as string[]}
          setMessages={setMessages}
          isError
        />
      )}
    </form>
  )
}

export default Update
